/** @format */

/**
 * 服务总览 — 顶部筛选条：聚合方式、时间范围、自动刷新。
 */

import React, { useEffect, useRef, useState } from 'react'
import { _$, Select, Option, Message, Icon } from '@king-design/react'
import styled from 'styled-components'
import QuickRangeDate, {
  EDIT_GRAPH_CHART_QUICK_RANGE,
  QuickRangeDateMethod
} from 'src/components/QuickRangeDate'
import { getRangeDateValue } from 'src/components/QuickRangeDate/constants'
import { ServiceOverviewSearchProps } from '../typescript'
import {
  DEFAULT_HEADER_FILTER_RANGE,
  HEADER_FILTER_QUICK_RANGE_KEYS,
  getAggregateTypeOptionsByTimeRange,
  coerceAggregateTypeForRange
} from '../constant'
import { secondsToText } from 'src/containers/MonitorDashboard/Customboard/constants'

/** 服务总览 — 顶部筛选条对外 props */
export interface HeaderFilterProps {
  className?: string
  value: ServiceOverviewSearchProps
  showAggregate?: boolean
  onChange: (search: ServiceOverviewSearchProps) => void
}

/** search.time 为秒；QuickRangeDate / 聚合粒度计算使用毫秒 */
function timeSecToMs(time: [number, number]): [number, number] {
  return [time[0] * 1000, time[1] * 1000]
}

const HeaderFilter: React.FC<HeaderFilterProps> = ({
  className,
  onChange,
  value,
  showAggregate = true
}) => {
  const [search, setSearch] = useState<ServiceOverviewSearchProps>(value)
  const [aggregateTypeOptions, setAggregateTypeOptions] = useState(() => {
    const [startMs, endMs] = timeSecToMs(value?.time as [number, number])
    return getAggregateTypeOptionsByTimeRange(startMs, endMs)
  })
  const [range, setRange] = useState<string>(DEFAULT_HEADER_FILTER_RANGE)
  const [autoRefresh, setAutoRefresh] = useState<number>(0)
  const [secondsText, setSecondsText] = useState<string>('')
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const quickDateRef = useRef<QuickRangeDateMethod>()
  const pendingAutoTickRef = useRef(false)

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    if (!autoRefresh || autoRefresh <= 0) {
      setSecondsText('')
      return
    }

    let remaining = autoRefresh
    setSecondsText(secondsToText(remaining))
    intervalRef.current = setInterval(() => {
      remaining -= 1
      if (remaining <= 0) {
        pendingAutoTickRef.current = true
        quickDateRef.current?.reloadDate?.()
        queueMicrotask(() => {
          if (pendingAutoTickRef.current) {
            pendingAutoTickRef.current = false
            onChange({ ...search })
          }
        })
        remaining = autoRefresh
      }
      setSecondsText(secondsToText(remaining))
    }, 1000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [autoRefresh])

  return (
    <div className={className}>
      <div className="header-col">
        <p className="header-col-label">{_$('时间范围')}：</p>
        <QuickRangeDate
          format="YYYY-MM-DD HH:mm"
          ref={quickDateRef}
          showShortcuts={false}
          optionalDateRange={90}
          dateDiff={31}
          defautlValue={range || timeSecToMs(search.time as [number, number])}
          changeValue={(date, r) => {
            const nextRange = r ?? ''
            const timeSec: [number, number] =
              nextRange && HEADER_FILTER_QUICK_RANGE_KEYS.includes(nextRange)
                ? getRangeDateValue(nextRange, true)
                : [Math.floor(date[0] / 1000), Math.floor(date[1] / 1000)]
            const [startMs, endMs] = timeSecToMs(timeSec)
            if (pendingAutoTickRef.current) {
              pendingAutoTickRef.current = false
            }
            const newAggregateTypeOptions = getAggregateTypeOptionsByTimeRange(startMs, endMs)
            setAggregateTypeOptions(newAggregateTypeOptions)
            const nextAgg = coerceAggregateTypeForRange(startMs, endMs, search.aggregateType)
            const nextSearch: ServiceOverviewSearchProps = {
              time: timeSec,
              aggregateType: nextAgg
            }
            setSearch(nextSearch)
            setRange(nextRange)
            onChange(nextSearch)
          }}
          // eslint-disable-next-line no-restricted-syntax
          quickRange={EDIT_GRAPH_CHART_QUICK_RANGE.filter(item => item !== '近30天')}
          clearable={false}
        />
      </div>
      {showAggregate ? (
        <div className="header-col">
          <p className="header-col-label">{_$('聚合方式')}：</p>
          <Select
            value={search.aggregateType}
            width={150}
            onChangeValue={value => {
              setSearch(prev => {
                const next = { ...prev, aggregateType: value }
                onChange(next)
                return next
              })
            }}
          >
            {aggregateTypeOptions.map(item => (
              <Option key={item.value} value={item.value}>
                {item.label}
              </Option>
            ))}
          </Select>
        </div>
      ) : null}
      <div className="header-col">
        <p className="header-col-label">{_$('自动刷新')}：</p>
        <Select
          disabled={!range}
          value={autoRefresh}
          onChangeValue={value => {
            setAutoRefresh(value)
            Message.success(_$('自动刷新设置成功'))
          }}
          width={100}
        >
          <Option value={0}>{_$('关闭')}</Option>
          <Option value={5}>{_$('5秒')}</Option>
          <Option value={10}>{_$('10秒')}</Option>
          <Option value={30}>{_$('30秒')}</Option>
          <Option value={60}>{_$('1分钟')}</Option>
          <Option value={300}> {_$('5分钟')}</Option>
        </Select>
        {autoRefresh > 0 && !!secondsText && (
          <div className="timer-box">
            <Icon className="ion-android-alarm-clock" />
            <span>{secondsText}</span>
          </div>
        )}
      </div>
    </div>
  )
}

/** 各分析页顶栏：时间范围、聚合粒度等 */
export default styled(HeaderFilter)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  .header-col {
    display: flex;
    align-items: center;
    margin-right: 16px;
    .header-col-label {
      margin-right: 8px;
      margin-bottom: 0;
    }
    .timer-box {
      display: flex;
      align-items: center;
      margin-left: 8px;
      .k-icon {
        font-size: 18px;
        margin-right: 2px;
      }
    }
  }
`
